// BATCH-NEXT-2 (Niranjan, Feb 2026) — sample-size-adjusted h² gate.
//
// Phase 2 (QTL detection) and Phase 4 (GS) both require traits with
// non-trivial heritability to deliver useful results. This helper
// reads Phase 1 results from the cross-phase session registry and
// surfaces a soft-disable banner when one or more of the picked
// traits falls below a sample-size-adjusted h² threshold.
//
// The threshold is *adjusted* (Option β from the spec):
//   Phase 4 gate: h² < clip(0.20 + 100/N, 0.20, 0.40)
//   Phase 2 gate: h² < clip(0.10 + 100/N, 0.10, 0.30)
// + a Lander–Botstein-style power proxy displayed in the banner so
// the breeder sees concrete numbers, not just a red flag.

import React, { useMemo, useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { getRegistry } from '@/utils/molecularSessionRegistry';


export function getPhase1TraitInfo(genotypeHash) {
  // Walk session registry → Phase 1 entry with same genotype hash.
  if (!genotypeHash) return null;
  const reg = getRegistry();
  for (const [, r] of Object.entries(reg)) {
    if (r.phase === 'phase1' && r.genotypeHash === genotypeHash && r.summary?.traits) {
      return r.summary;
    }
  }
  return null;
}


export function computeAdjustedThreshold(phase, nIndividuals = 200) {
  const N = Math.max(1, Number(nIndividuals) || 1);
  if (phase === 'gs') {
    return clip(0.20 + 100 / N, 0.20, 0.40);
  }
  // qtl
  return clip(0.10 + 100 / N, 0.10, 0.30);
}


function clip(x, lo, hi) {
  return Math.min(hi, Math.max(lo, x));
}


export function landerBotsteinPowerProxy({ N, h2, pveQtl = 0.10 }) {
  // Crude power proxy: 1 − exp(−2·N·h²·(PVE_QTL/0.10)). Not exact
  // Lander–Botstein, but monotone in the right direction and gives
  // the user something concrete instead of a binary gate.
  const arg = 2 * N * Math.max(0, h2) * (pveQtl / 0.10);
  return Math.max(0, Math.min(1, 1 - Math.exp(-arg)));
}


export function HeritabilityGatingBanner({
  phase,                // 'qtl' or 'gs'
  selectedTraits = [],  // names of traits the user picked
  phase1Summary,        // {traits: {trait → {h2, band}}, nIndividuals?}
  nIndividualsHint,     // optional override (e.g. from phenotype headcount)
  onProceedAnyway,      // callback when user toggles override
  proceedAnyway,        // controlled override flag from parent
  testid = 'mol-h2-gating-banner',
}) {
  const traits = phase1Summary?.traits || {};
  const N = nIndividualsHint || phase1Summary?.nIndividuals || 200;
  const threshold = useMemo(() => computeAdjustedThreshold(phase, N), [phase, N]);

  const lowH2 = useMemo(() => {
    const out = [];
    selectedTraits.forEach(t => {
      const r = traits[t];
      if (r && typeof r.h2 === 'number' && r.h2 < threshold) {
        out.push({ trait: t, h2: r.h2 });
      }
    });
    return out;
  }, [selectedTraits, traits, threshold]);

  const [open, setOpen] = useState(true);
  if (!lowH2.length) return null;

  return (
    <div
      className="rounded-lg px-4 py-3 mt-2"
      style={{
        background: '#FEF3C7',
        border: `1px solid ${proceedAnyway ? '#94A3B8' : '#F59E0B'}`,
        color: '#7C2D12',
      }}
      data-testid={testid}
    >
      <div className="flex items-start gap-2">
        {proceedAnyway
          ? <ShieldCheck size={14} className="mt-0.5 text-slate-500 shrink-0" />
          : <AlertTriangle size={14} className="mt-0.5 text-amber-600 shrink-0" />}
        <div className="text-xs leading-relaxed flex-1">
          <div className="font-bold mb-1">
            {phase === 'gs'
              ? 'Low heritability detected — Genomic Selection accuracy will be unreliable.'
              : 'Low heritability detected — QTL mapping will be statistically underpowered.'}
          </div>
          <div className="mb-2">
            Sample-size-adjusted threshold: <strong>h² ≥ {threshold.toFixed(2)}</strong>{' '}
            (n ≈ {N}). Trait{lowH2.length > 1 ? 's' : ''} below threshold:
            <ul className="list-disc list-inside mt-1 ml-2">
              {lowH2.map(({ trait, h2 }) => {
                const power = landerBotsteinPowerProxy({ N, h2 });
                return (
                  <li key={trait} data-testid={`${testid}-trait-${trait}`}>
                    <span className="font-mono">{trait}</span> — h² = {h2.toFixed(2)},
                    expected detection power ≈ {(power * 100).toFixed(0)}%
                  </li>
                );
              })}
            </ul>
          </div>
          {open && (
            <div className="text-[11px] mb-2 text-amber-900">
              Power proxy assumes PVE_QTL ≥ 10%. Below 50% threshold is recommended for reliable mapping.
              Lower thresholds risk false-negative results that look like an ineffective method when the
              underlying issue is data, not the algorithm.
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <button
              type="button"
              onClick={() => onProceedAnyway && onProceedAnyway(!proceedAnyway)}
              className="text-[11px] font-semibold rounded px-2.5 py-1 border transition-colors"
              style={{
                background: proceedAnyway ? '#94A3B8' : 'white',
                color: proceedAnyway ? 'white' : '#7C2D12',
                borderColor: proceedAnyway ? '#94A3B8' : '#F59E0B',
              }}
              data-testid={`${testid}-toggle`}
            >
              {proceedAnyway ? '✓ Proceeding anyway (override active)' : 'Run anyway (I understand the risk)'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="text-[11px] text-amber-700 underline hover:text-amber-900"
              data-testid={`${testid}-details-toggle`}
            >
              {open ? 'Hide details' : 'Show details'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
